import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { navigate } from 'gatsby'
import { Transition, TransitionGroup } from 'react-transition-group'
import PropTypes from 'prop-types'

import { ThemeProvider } from 'styled-components'

import { GlobalStyle } from 'styles'
import {
  LayoutWrapper,
  PageWrapper,
  ContentWrapper,
  ArrowLeft,
  ArrowRight,
  PrecachingInfoContainer,
  PrecachingLogo
} from './index.styled'

import 'styles/fonts.css'

import LayoutPasswordRequest from './password-request'

const getCurrentTheme = wideScreen => wideScreen
  ? ({ calculateResponsiveSize: (px) => `${px / 21.6}vh` })
  : ({ calculateResponsiveSize: (px) => `${px / 38.4}vw` })

const LayoutContent = ({ children, location }) => {
  return (
    <>
      <TransitionGroup component={null}>
        <Transition key={location.pathname} timeout={{ enter: 1000, exit: 1000 }}>
          {status => <div className={`page ${status}`}><LayoutWrapper id='layoutWrapper'>{children}</LayoutWrapper></div>}
        </Transition>
      </TransitionGroup>
    </>
  )
}

const Layout = ({ children, location, pageContext: { nextSlide, prevSlide, prevChapter, nextChapter, media, appVersion, password } }) => {
  const [allowedAccess, setAllowedAccess] = React.useState(!password)
  const [theme, setTheme] = useState(getCurrentTheme(true))
  const [precachingStatus, setPrecachingStatus] = useState(appVersion === 'client' ? 'preparing' : 'finished')
  const [precachingCount, setPrecachingCount] = useState(0)
  const mediaRef = useRef(media)
  const touchStartPosition = useRef(null)

  const prefetchingPercentage = useMemo(() => media?.length ? Math.round(100 * precachingCount / media.length) : 0, [media, precachingCount])
  const controls = useMemo(() => appVersion === 'client', [appVersion])

  const keyDownHandler = useCallback(
    event => {
      // console.log('Keyboard event', event.key)
      if (event.key === 'ArrowRight') {
        nextSlide && navigate(nextSlide)
      } else if (event.key === 'ArrowLeft') {
        prevSlide && navigate(prevSlide)
      } else if (event.key === 'ArrowUp' && !controls) {
        prevChapter && navigate(prevChapter)
      } else if (event.key === 'ArrowDown' && !controls) {
        nextChapter && navigate(nextChapter)
      }
    },
    [nextSlide, prevSlide, prevChapter, nextChapter, controls]
  )

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler)
    return () => window.removeEventListener('keydown', keyDownHandler)
  }, [keyDownHandler])

  const swipeHandler = useCallback(
    (start, end) => {
      const touchDelta = (start - end) / window.innerWidth
      if (Math.abs(touchDelta) > 0.01) {
        if (touchDelta > 0) {
          nextSlide && navigate(nextSlide)
        } else {
          prevSlide && navigate(prevSlide)
        }
      }
    },
    [nextSlide, prevSlide]
  )

  useEffect(() => {
    const touchStartHandler = event => {
      touchStartPosition.current = (event.touches[0] || event.changedTouches[0]).pageX
    }
    const touchEndHandler = event => {
      swipeHandler(touchStartPosition.current, (event.touches[0] || event.changedTouches[0]).pageX)
      touchStartPosition.current = null
    }
    const touchEndCancel = event => {
      touchStartPosition.current = null
    }

    window.addEventListener('touchstart', touchStartHandler)
    window.addEventListener('touchend', touchEndHandler)
    window.addEventListener('touchcancel', touchEndCancel)
    return () => {
      window.removeEventListener('touchstart', touchStartHandler)
      window.removeEventListener('touchend', touchEndHandler)
      window.removeEventListener('touchcancel', touchEndCancel)
    }
  }, [keyDownHandler, touchStartPosition, swipeHandler])

  useEffect(() => {
    let wideScreen
    const handleResize = () => {
      if ((window.innerWidth / window.innerHeight >= 1.7777777777777777) !== wideScreen) {
        wideScreen = (window.innerWidth / window.innerHeight >= 1.7777777777777777)
        setTheme(getCurrentTheme(wideScreen))
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (appVersion !== 'client' || precachingStatus !== 'preparing') {
      return
    }

    const precacheTimeout = setTimeout(() => {
      setPrecachingStatus('error')
    }, 3000)

    return () => {
      clearTimeout(precacheTimeout)
    }
  }, [precachingStatus, appVersion])

  useEffect(() => {
    if (appVersion !== 'client') {
      return
    }
    // console.log('MediaRef updated', mediaRef.current)
    if (!mediaRef.current?.length) {
      setPrecachingStatus('finished')
      return () => {}
    }

    navigator.serviceWorker.ready.then(registration => {
      setPrecachingStatus('starting')
      navigator.serviceWorker.onmessage = event => {
        // console.log('Message received:', event.data)
        if (event.data.type === 'precache-progress') {
          setPrecachingCount(event.data.count)
        } else if (event.data.type === 'precache-status') {
          setPrecachingStatus(event.data.status)
        }
      }
      // console.log('Posting message to worker')
      registration.active.postMessage(
        { type: 'deck-precache', urls: mediaRef.current }
      )
    })
  }, [mediaRef, appVersion])

  return (
    <PageWrapper>
      <GlobalStyle />
      <ThemeProvider theme={theme}>
        <ContentWrapper>
          {
            !allowedAccess
              ? (<LayoutPasswordRequest password={password} onLoginSuccess={() => setAllowedAccess(true)} />)
              : (
                  precachingStatus !== 'finished' && precachingStatus !== 'error'
                    ? (
                      <PrecachingInfoContainer>
                        <PrecachingLogo />
                        Loading content: {prefetchingPercentage}%
                      </PrecachingInfoContainer>
                      )
                    : (
                      <>
                        <LayoutContent location={location}>{children}</LayoutContent>
                        {controls && prevSlide && <ArrowLeft to={prevSlide} />}
                        {controls && nextSlide && <ArrowRight to={nextSlide} />}
                      </>
                      )
                )
          }
        </ContentWrapper>
      </ThemeProvider>
    </PageWrapper>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired
}

export default Layout
